"use client";
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandList,
  CommandItem,
  CommandEmpty,
} from "@/components/ui/command";
import { X, Search } from "lucide-react";
import { cn } from "@/lib/utils";

interface SpecialtyComboboxProps {
  value: string;
  onChange: (val: string) => void;
}

export function SpecialtyCombobox({ value, onChange }: SpecialtyComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [input, setInput] = React.useState("");
  const containerRef = React.useRef<HTMLDivElement>(null);

  const { data } = useQuery<{
    ok: boolean;
    items: { id: number; name: string }[];
  }>({
    queryKey: ["specialties-list"],
    queryFn: async () => {
      const res = await fetch("/api/dashboard/specialties");
      if (!res.ok) throw new Error("Failed specialties");
      return res.json();
    },
    staleTime: 60_000,
  });
  const specialties = React.useMemo(() => data?.items || [], [data]);

  const suggestions = React.useMemo(() => {
    const term = input.trim().toLowerCase();
    if (!term) return specialties.slice(0, 12);
    return specialties
      .filter((s) => s.name.toLowerCase().includes(term))
      .slice(0, 12);
  }, [specialties, input]);

  React.useEffect(() => {
    function handlePointer(e: MouseEvent | PointerEvent) {
      if (!open) return;
      const el = containerRef.current;
      if (el && !el.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("pointerdown", handlePointer);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("pointerdown", handlePointer);
    };
  }, [open]);

  function handleBlur() {
    setTimeout(() => {
      const el = containerRef.current;
      if (!el) return;
      if (open && !el.contains(document.activeElement)) setOpen(false);
    }, 40);
  }

  function handleSelect(name: string) {
    onChange(name);
    setInput(name);
    setOpen(false);
  }
  function clear() {
    onChange("");
    setInput("");
  }

  return (
    <div ref={containerRef} className="space-y-2" onBlur={handleBlur}>
      <label className="text-sm font-medium flex items-center gap-1">
        Specialty Search
        <span className="text-[10px] text-muted-foreground font-normal">
          (matches partial)
        </span>
      </label>
      <div className="relative">
        <div className="flex gap-2">
          <Input
            placeholder="e.g. Plaster, Microcement, Venetian"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              onChange(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className="flex-1"
          />
          {value && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clear}
              aria-label="Clear specialty"
              className="shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        {open && (
          <div
            className={cn(
              "absolute z-40 mt-2 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md",
              "animate-in fade-in-0 zoom-in-95"
            )}
          >
            <Command
              shouldFilter={false}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
              }}
            >
              <div className="flex items-center px-3 pb-2 pt-3">
                <Search className="h-4 w-4 text-muted-foreground mr-2" />
                <p className="text-xs text-muted-foreground">
                  {suggestions.length} suggestions
                </p>
              </div>
              <CommandList className="max-h-56">
                {suggestions.length === 0 && (
                  <CommandEmpty>No matches.</CommandEmpty>
                )}
                {suggestions.map((s) => (
                  <CommandItem
                    key={s.id}
                    value={s.name}
                    onSelect={() => handleSelect(s.name)}
                    className="cursor-pointer"
                  >
                    {s.name}
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </div>
        )}
      </div>
    </div>
  );
}
